import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useDebounce } from '../../../common/Debounce.hooks';
import { ProductComponent } from '../Product.interface';
import { useFindProductComponents } from '../Product.hooks';

// compare IDs of both arrays
function areIDsEqual<Entity>(
  arr1: Entity[],
  arr2: Entity[],
  mapEntityId: (entity: Entity) => number = (entity: Entity) => {
    return (entity as any).id;
  }
): boolean {
  return isEqual(arr1.map(mapEntityId).sort(), arr2.map(mapEntityId).sort());
}

export interface ProductComponentsSelectorProps {
  productId: number;
  disabled?: boolean;
  value?: ProductComponent[];
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onSelected?: (components: ProductComponent[]) => void;
  onChange?: (components: ProductComponent[]) => void;
}

const ProductComponentsSelector: React.FC<ProductComponentsSelectorProps> = (
  props: ProductComponentsSelectorProps
) => {
  const {
    productId,
    value: initialComponents,
    disabled,
    textFieldProps,
    onChange,
    onSelected
  } = props;
  const previousComponents = useRef<ProductComponent[]>(
    initialComponents || []
  );
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<ProductComponent[]>([]);
  const [components, setComponents] = useState<ProductComponent[]>([]);
  const debouncedSearch: string = useDebounce(search, 300);
  const {
    productComponents: data,
    isLoading,
    loadingError: error,
    findProductComponents: get
  } = useFindProductComponents(productId);

  const params: ApiParams = useMemo(() => {
    const params: ApiParams = {};
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    return params;
  }, [debouncedSearch]);

  useEffect(() => {
    get(params);
  }, [get, params]);

  useEffect(() => {
    data && setComponents(data);
  }, [data]);

  // if initialComponentIds changed, reselect components
  useEffect(() => {
    if (!areIDsEqual(previousComponents.current, initialComponents || [])) {
      setSelected([]);
      previousComponents.current = initialComponents || [];
      // refetch
      get(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialComponents]);

  useEffect(() => {
    // if no selected component yet
    if (selected.length > 0) {
      return;
    }
    const selectedComponents: ProductComponent[] = components.filter(
      (component: ProductComponent) => {
        return initialComponents?.some(
          (c: ProductComponent) => c.id === component.id
        );
      }
    );
    if (selectedComponents.length > 0) {
      setSelected(selectedComponents);
      onSelected?.(selectedComponents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components, initialComponents]);

  return (
    <Autocomplete
      multiple
      loading={isLoading}
      disabled={disabled}
      value={selected}
      options={components}
      getOptionLabel={(option: ProductComponent) => option.name}
      getOptionSelected={(option: ProductComponent) =>
        !!initialComponents?.some(
          (component: ProductComponent) => component.id === option.id
        )
      }
      onInputChange={(_, value: string) => setSearch(value)}
      onChange={(_event: React.ChangeEvent<{}>, value: ProductComponent[]) => {
        previousComponents.current = value;
        setSelected(value);
        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Product Components'
          placeholder='Choose Product Components'
          helperText={error?.message}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default ProductComponentsSelector;
